import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, Loader2, X } from 'lucide-react';
import api, { apiErrorText } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { normalizePhoneInput, PHONE_INPUT_HTML_PATTERN } from '../../utils/phone';

const ApplicationForm: React.FC = () => {
  const { t } = useTranslation();
  const { tenantSlug, positionId } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin_url: '',
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const fetchPosition = async () => {
      try {
        const response = await api.get(`/api/positions/public/${tenantSlug}/positions`);
        const pos = response.data.positions.find((p: any) => p.id === parseInt(positionId!));
        setPosition(pos);
      } catch (error) {
        console.error('Failed to fetch position:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosition();
  }, [tenantSlug, positionId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData({ ...formData, phone: normalizePhoneInput(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCvFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (!cvFile) {
      setError(t('applicationForm.cvRequired'));
      setSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    if (formData.phone) {
      formDataToSend.append('phone', formData.phone);
    }
    formDataToSend.append('linkedin_url', formData.linkedin_url);
    formDataToSend.append('cv_file', cvFile);

    setSuccess(true);
    redirectTimeoutRef.current = setTimeout(() => {
      navigate(`/careers/${tenantSlug}`);
    }, 2000);

    api
      .post(`/api/applications/apply/${tenantSlug}/${positionId}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .catch((err: any) => {
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
        setSuccess(false);
        setError(apiErrorText(err.response?.data?.detail) || t('applicationForm.failed'));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <Loader2 className="h-10 w-10 animate-spin text-lotus-gold" />
          <p>{t('applicationForm.loading')}</p>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('applicationForm.notFound')}</h1>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('applicationForm.submitted')}</h2>
          <p className="text-gray-600 mb-4">
            {t('applicationForm.thankYou')}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800 text-left">
            {t('applicationForm.withdrawInfo')}
          </div>
          <button
            onClick={() => navigate(`/careers/${tenantSlug}`)}
            className="btn-primary w-full"
          >
            {t('applicationForm.backToCareer')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="card">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{position.title}</h1>
          <p className="text-gray-600 mb-8">{t('applicationForm.fillForm')}</p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label">{t('applicationForm.fullName')} *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">{t('common.email')} *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">{t('common.phone')}</label>
              <input
                type="tel"
                name="phone"
                pattern={PHONE_INPUT_HTML_PATTERN}
                value={formData.phone}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="label">{t('applicationForm.linkedin')}</label>
              <input
                type="url"
                name="linkedin_url"
                value={formData.linkedin_url}
                onChange={handleChange}
                className="input"
                placeholder={t('applicationForm.linkedinPlaceholder')}
              />
            </div>

            <div>
              <label className="label">{t('applicationForm.cv')} *</label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-lotus-gold transition-colors">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-lotus-gold hover:text-lotus-gold-dark">
                      <span>{t('applicationForm.uploadFile')}</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        required
                      />
                    </label>
                    <p className="pl-1">{t('applicationForm.dragDrop')}</p>
                  </div>
                  <p className="text-xs text-gray-500">{t('applicationForm.fileTypes')}</p>
                  {cvFile && (
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <p className="text-sm text-lotus-gold font-medium">{cvFile.name}</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCvFile(null);
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50"
                        title={t('common.remove')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-primary"
            >
              {submitting ? t('applicationForm.submitting') : t('applicationForm.submitButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
